import React, { useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from './ui/sheet';
import { Inbox, Clock, CheckCircle2, XCircle, ExternalLink, Loader2, MessageSquare } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import OfferChatDialog from './OfferChatDialog';

const STATUS_BADGE = {
  pending:  { label: 'Pending',  cls: 'bg-yellow-300 text-[#3a1064]', icon: Clock },
  accepted: { label: 'Accepted', cls: 'bg-emerald-500 text-white',     icon: CheckCircle2 },
  declined: { label: 'Declined', cls: 'bg-rose-500 text-white',         icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function MyOffersSheet({ trigger }) {
  const { isAuthed } = useAuth();
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatOffer, setChatOffer] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const load = async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const list = await api.listMyOffers();
      setOffers(list);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const openChat = (o) => {
    setChatOffer(o);
    setChatOpen(true);
  };

  const handleOfferChange = (updated) => {
    setOffers((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setChatOffer(updated);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        data-testid="my-offers-sheet"
        className="bg-[#fff7d6] border-l-[3px] border-[#3a1064] w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="bg-[#ffd900] border-b-[3px] border-[#3a1064] px-5 py-4">
          <SheetTitle className="text-[#3a1064] font-black text-xl flex items-center gap-2">
            <Inbox className="h-5 w-5 text-[#ff2bd6]" /> My Offers
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="py-10 text-center text-[#3a1064]/70">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            </div>
          ) : offers.length === 0 ? (
            <div className="py-10 text-center text-[#3a1064]/70 font-bold">
              You haven&apos;t made any offers yet.
              <br />
              <span className="text-xs">Use the &ldquo;Buy now&rdquo; section to make one.</span>
            </div>
          ) : (
            offers.map((o) => {
              const s = STATUS_BADGE[o.status] || STATUS_BADGE.pending;
              const Icon = s.icon;
              return (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`my-offer-${o.id}`}
                  onClick={() => openChat(o)}
                  className="w-full text-left bg-white rounded-xl border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] p-3 hover:bg-yellow-50 hover:-translate-y-0.5 transition-transform cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <img src={o.sprite_img} alt="" className="h-14 w-14 object-contain bg-yellow-50 rounded-lg border-2 border-[#3a1064] p-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-black text-[#3a1064] truncate">{o.sprite_name}</div>
                        <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full flex items-center gap-1 ${s.cls}`}>
                          <Icon className="h-2.5 w-2.5" /> {s.label}
                        </span>
                      </div>
                      <div className="text-xs font-bold text-[#3a1064]/70 mt-0.5">
                        Offered <span className="text-[#3a1064] font-black">${o.offer_amount.toFixed(2)}</span>
                        {o.suggested_price ? <span> · suggested ${o.suggested_price.toFixed(2)}</span> : null}
                      </div>
                      <div className="text-[10px] font-bold text-[#3a1064]/50 mt-0.5">{fmtDate(o.created_at)}</div>
                    </div>
                  </div>

                  {o.status === 'accepted' && o.payment_link && (
                    <div className="mt-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg p-2 space-y-1.5">
                      <div className="inline-flex items-center gap-1.5 bg-emerald-500 text-white font-black text-xs px-3 py-1.5 rounded-lg border-2 border-[#3a1064] shadow-[0_2px_0_#3a1064]">
                        <ExternalLink className="h-3 w-3" /> Pay now (open chat)
                      </div>
                      {o.meet_up_note && (
                        <div className="text-xs font-bold text-[#3a1064]"><span className="font-black uppercase text-[9px] tracking-wider">Meet-up:</span> {o.meet_up_note}</div>
                      )}
                    </div>
                  )}

                  {o.status === 'declined' && o.admin_reply && (
                    <div className="mt-3 bg-rose-50 border-2 border-rose-300 rounded-lg p-2 text-xs font-bold text-[#3a1064]">
                      <span className="font-black uppercase text-[9px] tracking-wider">Reply:</span> {o.admin_reply}
                    </div>
                  )}

                  {o.message && (
                    <div className="mt-2 text-[11px] font-bold text-[#3a1064]/70 italic">
                      &ldquo;{o.message}&rdquo;
                    </div>
                  )}

                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-black text-[#ff2bd6]">
                    <MessageSquare className="h-3.5 w-3.5" /> Open chat
                  </div>
                </button>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
    <OfferChatDialog
      open={chatOpen}
      onOpenChange={(v) => { setChatOpen(v); if (!v) load(); }}
      offer={chatOffer}
      onOfferChange={handleOfferChange}
    />
    </>
  );
}
